import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, tokenStore } from './api';
import type { AuthenticatedUser } from './types';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  loading: boolean;
  error: string | null;
  requestMagicLink: (email: string) => Promise<{ devMagicUrl?: string }>;
  loginUsuario: (usuario: string, senha: string) => Promise<AuthenticatedUser>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchMe() {
    try {
      const { user, token } = await api.auth.me();
      // Sessão deslizante (hardening 08/07): /me devolve token novo perto de expirar
      if (token) tokenStore.set(token);
      setUser(user);
      (window as unknown as { __apexUserId?: string; __apexCanVerValores?: boolean }).__apexUserId = user.pessoaId;
      (window as unknown as { __apexCanVerValores?: boolean }).__apexCanVerValores = user.canVerValores === true;
    } catch {
      setUser(null);
      tokenStore.clear();
    }
  }

  useEffect(() => {
    let mounted = true;
    fetchMe().finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  async function requestMagicLink(email: string) {
    setError(null);
    try {
      const res = await api.auth.magicLink(email);
      return { devMagicUrl: res.devMagicUrl };
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.body as { message?: string })?.message ?? e.message
        : String(e);
      setError(msg);
      throw e;
    }
  }

  async function loginUsuario(usuario: string, senha: string) {
    setError(null);
    try {
      const { token, user } = await api.auth.login(usuario, senha);
      tokenStore.set(token);
      setUser(user);
      (window as unknown as { __apexUserId?: string; __apexCanVerValores?: boolean }).__apexUserId = user.pessoaId;
      (window as unknown as { __apexCanVerValores?: boolean }).__apexCanVerValores = user.canVerValores === true;
      return user;
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.body as { message?: string })?.message ?? e.message
        : String(e);
      setError(msg);
      throw e;
    }
  }

  async function logout() {
    try { await api.auth.logout(); } catch { /* noop */ }
    tokenStore.clear();
    setUser(null);
    (window as unknown as { __apexCanVerValores?: boolean }).__apexCanVerValores = false;
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, requestMagicLink, loginUsuario, refresh: fetchMe, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
